import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatApiError } from "@/lib/AuthContext";
import { Card, Btn, Pill, Modal, Empty, inputCls } from "@/dashboard/ui";
import { LayoutTemplate, Sparkles, Pencil, Trash2, Globe, GlobeLock, BarChart3, Monitor, Smartphone, ImagePlus, XCircle } from "lucide-react";
import { LandingPageView } from "@/pages/PublicLandingPage";

const TEMPLATES = [
  { id: "classic", label: "Classic", swatch: "#111111" },
  { id: "warm", label: "Warm", swatch: "#d97706" },
  { id: "fresh", label: "Fresh", swatch: "#10b981" },
];

function EditorImage({ mediaId, onChange }) {
  const [url, setUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let objectUrl = null;
    if (mediaId) {
      api.get(`/marketing/media/${mediaId}`, { responseType: "blob" })
        .then((r) => { objectUrl = URL.createObjectURL(r.data); setUrl(objectUrl); })
        .catch(() => setUrl(null));
    } else setUrl(null);
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [mediaId]);

  const upload = async (f) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/marketing/media", fd);
      if (data.kind !== "image") throw new Error("Landing pages use images only");
      onChange(data.id);
    } catch (e) {
      toast.error(e.message || formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="lp-image-picker">
      {url ? (
        <div className="relative inline-block">
          <img src={url} alt="Page" className="h-24 rounded-xl border border-neutral-200 object-cover" />
          <button type="button" title="Remove image" data-testid="lp-image-remove" onClick={() => onChange(null)}
            className="absolute -right-1.5 -top-1.5 rounded-full bg-white text-neutral-400 shadow hover:text-red-600">
            <XCircle size={18} />
          </button>
        </div>
      ) : (
        <label className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-neutral-300 px-3.5 py-1.5 text-xs font-semibold text-neutral-600 ${busy ? "opacity-60" : ""}`} data-testid="lp-image-upload">
          <ImagePlus size={13} /> {busy ? "Uploading…" : "Upload image"}
          <input type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" disabled={busy}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }} />
        </label>
      )}
    </div>
  );
}

function PageEditor({ page, onClose, onSaved }) {
  const [p, setP] = useState(() => JSON.parse(JSON.stringify(page)));
  const [preview, setPreview] = useState("desktop");
  const [saving, setSaving] = useState(false);
  const setS = (k, v) => setP((x) => ({ ...x, sections: { ...x.sections, [k]: v } }));
  const setList = (k, text) => setS(k, text.split("\n").filter((l) => l.trim()));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/lp/pages/${p.id}`, { title: p.title, sections: p.sections, theme: p.theme, consent_text: p.consent_text });
      toast.success("Page saved");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={`Edit page — ${p.page_type === "booking" ? "Appointment booking" : "Lead generation"}`} onClose={onClose} testid="lp-editor-modal">
      <div className="max-h-[75vh] space-y-3 overflow-y-auto pr-1">
        <label className="block">
          <span className="mb-1 block text-xs font-semibold text-neutral-600">Page title</span>
          <input className={inputCls} value={p.title} data-testid="lp-edit-title" onChange={(e) => setP((x) => ({ ...x, title: e.target.value }))} />
        </label>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-neutral-600">Headline</span>
            <input className={inputCls} value={p.sections.headline || ""} data-testid="lp-edit-headline" onChange={(e) => setS("headline", e.target.value)} />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-neutral-600">Button text</span>
            <input className={inputCls} value={p.sections.cta_text || ""} data-testid="lp-edit-cta" onChange={(e) => setS("cta_text", e.target.value)} />
          </label>
        </div>
        <label className="block">
          <span className="mb-1 block text-xs font-semibold text-neutral-600">Subheadline</span>
          <textarea rows={2} className={inputCls} value={p.sections.subheadline || ""} data-testid="lp-edit-sub" onChange={(e) => setS("subheadline", e.target.value)} />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-semibold text-neutral-600">Benefits (one per line)</span>
          <textarea rows={3} className={inputCls} value={(p.sections.benefits || []).join("\n")} data-testid="lp-edit-benefits" onChange={(e) => setList("benefits", e.target.value)} />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-semibold text-neutral-600">Services (name — description, one per line)</span>
          <textarea rows={3} className={inputCls} data-testid="lp-edit-services"
            value={(p.sections.services || []).map((x) => `${x.name} — ${x.description || ""}`).join("\n")}
            onChange={(e) => setS("services", e.target.value.split("\n").filter((l) => l.trim()).map((l) => {
              const [name = "", description = ""] = l.split("—").map((x) => x.trim());
              return { name, description };
            }))} />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-semibold text-neutral-600">About</span>
          <textarea rows={2} className={inputCls} value={p.sections.about || ""} data-testid="lp-edit-about" onChange={(e) => setS("about", e.target.value)} />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-semibold text-neutral-600">FAQs (question | answer, one per line)</span>
          <textarea rows={3} className={inputCls} data-testid="lp-edit-faqs"
            value={(p.sections.faqs || []).map((f) => `${f.q} | ${f.a}`).join("\n")}
            onChange={(e) => setS("faqs", e.target.value.split("\n").filter((l) => l.includes("|")).map((l) => {
              const [q = "", a = ""] = l.split("|").map((x) => x.trim());
              return { q, a };
            }))} />
        </label>
        <div>
          <span className="mb-1 block text-xs font-semibold text-neutral-600">Hero image</span>
          <EditorImage mediaId={p.sections.image_media_id} onChange={(id) => setS("image_media_id", id)} />
        </div>
        <label className="block">
          <span className="mb-1 block text-xs font-semibold text-neutral-600">Consent text on the form</span>
          <input className={inputCls} value={p.consent_text || ""} data-testid="lp-edit-consent" onChange={(e) => setP((x) => ({ ...x, consent_text: e.target.value }))} />
        </label>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-semibold text-neutral-600">Preview</span>
            <div className="flex rounded-full border border-neutral-300 p-0.5">
              <button type="button" data-testid="lp-preview-desktop" onClick={() => setPreview("desktop")}
                className={`rounded-full px-2.5 py-1 text-xs ${preview === "desktop" ? "bg-[#111111] text-white" : "text-neutral-500"}`}><Monitor size={12} /></button>
              <button type="button" data-testid="lp-preview-mobile" onClick={() => setPreview("mobile")}
                className={`rounded-full px-2.5 py-1 text-xs ${preview === "mobile" ? "bg-[#111111] text-white" : "text-neutral-500"}`}><Smartphone size={12} /></button>
            </div>
          </div>
          <div className={`overflow-hidden rounded-xl border border-neutral-200 ${preview === "mobile" ? "mx-auto max-w-[280px]" : ""}`} data-testid="lp-preview">
            <LandingPageView page={p} compact
              formSlot={<div className="rounded-xl border border-dashed border-neutral-300 p-3 text-center text-[11px] text-neutral-400">{p.page_type === "booking" ? "Booking widget appears here when published" : "Enquiry form appears here when published"}</div>} />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn data-testid="lp-edit-save" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save page"}</Btn>
        </div>
      </div>
    </Modal>
  );
}

export default function LandingPagesPanel() {
  const [pages, setPages] = useState(null);
  const [gen, setGen] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [stats, setStats] = useState(null);

  const load = () => api.get("/lp/pages").then((r) => setPages(r.data.pages)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const generate = async () => {
    setBusy(true);
    try {
      await api.post("/lp/generate", gen);
      toast.success("Page draft generated — review and edit it before publishing");
      setGen(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (p, status) => {
    try {
      await api.post(`/lp/pages/${p.id}/status`, { status });
      toast.success(status === "published" ? "Page is now live at its public link" : "Page unpublished — the public link now returns not found");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete landing page "${p.title}"? Its stats go with it.`)) return;
    await api.delete(`/lp/pages/${p.id}`);
    load();
  };

  const showStats = async (p) => {
    try {
      const { data } = await api.get(`/lp/pages/${p.id}/stats`);
      setStats({ page: p, ...data });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const publicUrl = (p) => `${window.location.origin}/lp/${p.slug}`;

  return (
    <div className="space-y-5" data-testid="landing-pages-panel">
      <div className="flex justify-end">
        <Btn data-testid="lp-new-button" onClick={() => setGen({ goal: "", page_type: "lead", template: "classic" })}>
          <LayoutTemplate size={15} /> New landing page
        </Btn>
      </div>

      {pages === null ? <p className="py-8 text-center text-sm text-neutral-400">Loading…</p> : pages.length === 0 ? (
        <Empty testid="lp-empty" icon={LayoutTemplate} title="No landing pages yet"
          body="Generate an AI draft for lead capture or appointment booking, edit it, preview it, then publish when you're happy." />
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {pages.map((p) => (
            <Card key={p.id} className="p-5" data-testid={`lp-card-${p.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-bold text-[#111111]">{p.title}</p>
                  <Pill tone={p.status === "published" ? "green" : "grey"} data-testid={`lp-status-${p.id}`}>
                    {p.status === "published" ? "Published" : "Draft"}
                  </Pill>
                  <Pill tone="outline">{p.page_type === "booking" ? "Booking" : "Lead capture"}</Pill>
                </div>
                <div className="flex shrink-0 gap-1">
                  <button className="flex h-8 w-8 items-center justify-center rounded-full text-neutral-500 hover:bg-neutral-100" title="Stats" data-testid={`lp-stats-${p.id}`} onClick={() => showStats(p)}><BarChart3 size={14} /></button>
                  <button className="flex h-8 w-8 items-center justify-center rounded-full text-neutral-500 hover:bg-neutral-100" title="Edit" data-testid={`lp-edit-${p.id}`} onClick={() => setEditing(p)}><Pencil size={14} /></button>
                  <button className="flex h-8 w-8 items-center justify-center rounded-full text-neutral-500 hover:bg-red-50 hover:text-red-600" title="Delete" data-testid={`lp-delete-${p.id}`} onClick={() => remove(p)}><Trash2 size={14} /></button>
                </div>
              </div>
              <p className="mt-1.5 truncate text-xs text-neutral-500">{p.sections?.headline}</p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {p.status === "published" ? (
                  <>
                    <a href={publicUrl(p)} target="_blank" rel="noreferrer" className="text-xs font-semibold text-neutral-600 underline" data-testid={`lp-open-${p.id}`}>Open public page</a>
                    <Btn variant="ghost" data-testid={`lp-unpublish-${p.id}`} onClick={() => setStatus(p, "draft")}>
                      <GlobeLock size={13} className="mr-1 inline" /> Unpublish
                    </Btn>
                  </>
                ) : (
                  <Btn variant="ghost" data-testid={`lp-publish-${p.id}`} onClick={() => setStatus(p, "published")}>
                    <Globe size={13} className="mr-1 inline" /> Publish
                  </Btn>
                )}
                <button className="text-xs text-neutral-400 hover:text-neutral-700" data-testid={`lp-copy-${p.id}`}
                  onClick={() => { navigator.clipboard?.writeText(publicUrl(p)); toast.success("Public link copied"); }}>
                  Copy link
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}
      <p className="text-[11px] text-neutral-400">Drafts are private — only you can see them. Publishing makes the page reachable at its public link; unpublishing takes it offline instantly. Enquiries land in your CRM and bookings in your diary.</p>

      {gen && (
        <Modal title="New landing page" onClose={() => setGen(null)} testid="lp-generate-modal">
          <div className="space-y-3">
            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-neutral-600">Campaign goal *</span>
              <input className={inputCls} value={gen.goal} data-testid="lp-gen-goal" placeholder="e.g. Fill weekday appointment gaps" onChange={(e) => setGen((g) => ({ ...g, goal: e.target.value }))} />
            </label>
            <div>
              <span className="mb-1 block text-xs font-semibold text-neutral-600">Page type</span>
              <div className="flex gap-1.5">
                {[["lead", "Lead generation (enquiry form)"], ["booking", "Appointment booking"]].map(([id, label]) => (
                  <button key={id} type="button" data-testid={`lp-gen-type-${id}`} onClick={() => setGen((g) => ({ ...g, page_type: id }))}
                    className={`rounded-full border px-3.5 py-1.5 text-xs font-medium ${gen.page_type === id ? "border-[#111111] bg-[#111111] text-white" : "border-neutral-300 text-neutral-600"}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <span className="mb-1 block text-xs font-semibold text-neutral-600">Template</span>
              <div className="flex gap-1.5" data-testid="lp-gen-templates">
                {TEMPLATES.map((t) => (
                  <button key={t.id} type="button" data-testid={`lp-gen-template-${t.id}`} onClick={() => setGen((g) => ({ ...g, template: t.id }))}
                    className={`flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-medium ${gen.template === t.id ? "border-[#111111]" : "border-neutral-300"}`}>
                    <span className="h-3 w-3 rounded-full" style={{ background: t.swatch }} /> {t.label}
                  </button>
                ))}
              </div>
            </div>
            <p className="text-[11px] text-neutral-400">The AI uses your marketing profile (business, services, location, voice). It never invents testimonials, awards or results.</p>
            <div className="flex justify-end gap-2">
              <Btn variant="ghost" onClick={() => setGen(null)}>Cancel</Btn>
              <Btn data-testid="lp-gen-submit" disabled={busy || !gen.goal.trim()} onClick={generate}>
                <Sparkles size={14} className="mr-1 inline" /> {busy ? "Generating…" : "Generate draft"}
              </Btn>
            </div>
          </div>
        </Modal>
      )}

      {editing && <PageEditor page={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}

      {stats && (
        <Modal title={`Page stats — ${stats.page.title}`} onClose={() => setStats(null)} testid="lp-stats-modal">
          <div className="space-y-4" data-testid="lp-stats-body">
            <div className="grid grid-cols-3 gap-3 text-center">
              {[["Views", stats.views], ["Enquiries", stats.submissions], ["Bookings", stats.bookings]].map(([l, v]) => (
                <div key={l} className="rounded-xl bg-neutral-50 p-3">
                  <p className="text-xl font-extrabold text-[#111111]" data-testid={`lp-stat-${l.toLowerCase()}`}>{v}</p>
                  <p className="text-[11px] text-neutral-500">{l}</p>
                </div>
              ))}
            </div>
            <div className="flex gap-3 text-center">
              <div className="flex-1 rounded-xl border border-neutral-200 p-3">
                <p className="text-lg font-bold">{stats.view_to_lead === null ? "—" : `${stats.view_to_lead}%`}</p>
                <p className="text-[11px] text-neutral-500">View → enquiry</p>
              </div>
              <div className="flex-1 rounded-xl border border-neutral-200 p-3">
                <p className="text-lg font-bold">{stats.view_to_booking === null ? "—" : `${stats.view_to_booking}%`}</p>
                <p className="text-[11px] text-neutral-500">View → booking</p>
              </div>
            </div>
            {Object.keys(stats.utm).length > 0 && (
              <div>
                <p className="mb-1 text-xs font-bold uppercase tracking-wider text-neutral-500">By campaign source (UTM)</p>
                {Object.entries(stats.utm).map(([src, v]) => (
                  <p key={src} className="text-xs text-neutral-600" data-testid={`lp-utm-${src}`}>{src}: {v.views} views · {v.submissions} enquiries · {v.bookings} bookings</p>
                ))}
              </div>
            )}
            <p className="text-[11px] text-neutral-400">{stats.data_note}</p>
          </div>
        </Modal>
      )}
    </div>
  );
}
